import {
  Badge,
  Box,
  Button,
  Center,
  IconButton,
  Menu,
  MenuButton, MenuItem,
  MenuList,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DataTable } from "../../../../components/DataTable";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDate,
  getSortDirection, showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS,
  USER_STATUSES,
  USER_TYPES
} from "../../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import {
  supplierAdministrationKeys, useActivateUser,
  useCreateSupplierUser, useDeactivateUser, useGetSupplierUsers, useResendSupplierInvitation,
  useUpdateSupplierUser
} from "../../../../hooks/query/supplier-administration";
import { MultiSelectFilter } from "../../../../components/DataTable/components";
import { FaBars, FaEnvelope, FaPen, FaPlay, FaTimes } from "react-icons/fa";
import { useGetUsersRoles, useGetUsersStatuses } from "../../../../hooks/query/user-administration";
import { SupplierUserModal } from "./index";

function SupplierUsers() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement.supplierUsers"
  });

  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [suppliersList, setSuppliersList] = useState([]);
  const [supplierModalData, setSupplierModalData] = useState(undefined);
  const {
    isOpen: isSupplierModalOpen,
    onOpen: onSupplierModalOpen,
    onClose: onSupplierModalClose
  } = useDisclosure();

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`activeStatus,${SORT_DIRECTIONS.ASC}`, `supplier,${SORT_DIRECTIONS.ASC}`, `username,${SORT_DIRECTIONS.ASC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const calcState = (prevState) => {
      let changedAttributes = {};

      Object.keys(newValObject).forEach(dataTableAttribute => {
        const value = newValObject[dataTableAttribute];

        if (dataTableAttribute === "filterObj") {
          // We have to save not only the new value sent from the data table
          // but also the previously applied filters, which we get from prevState
          changedAttributes[dataTableAttribute] = {
            ...prevState[dataTableAttribute],
            [value.attribute]: value.value
          };
        } else if (dataTableAttribute === "queryPageSortBy") {
          let stringValue = value[0];
          let accessorName = stringValue.split(",")[0];
          const sortDirection = stringValue.split(",")[1];
          const updatedArray = [...prevState[dataTableAttribute]];

          if (accessorName === "status") {
            // Back-end requires the usage of 'activeStatus' instead of 'status' for sorting
            stringValue = `activeStatus,${sortDirection}`;
            accessorName = "activeStatus";
          }

          const indexOfExisting = updatedArray.findIndex(el => {
            return el.split(",")[0] === accessorName;
          });
          if (indexOfExisting > -1) {
            // Column which we try to sort is already in the sort list, so we just manipulate it's record
            if (sortDirection === SORT_DIRECTIONS.NONE) {
              updatedArray.splice(indexOfExisting, 1);
            } else {
              updatedArray[indexOfExisting] = stringValue;
            }
          } else {
            // Column which we try to sort does not exist in the sort list, so we add it
            updatedArray.push(stringValue);
          }

          changedAttributes[dataTableAttribute] = updatedArray;
        } else {
          // All other values have primitive types, so we apply them directly
          // without checking their previous states
          changedAttributes[dataTableAttribute] = value;
        }
      });

      return changedAttributes;
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calcState(prevState)
      };
    });
  };

  const constructQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    // "status", "roles" and "sort" params are extracted from queryParams object, because they can have more than one value.
    // To avoid overriding of those multiple values which have to share the same key ("status", "roles" or "sort"), we put them
    // together in a string called "multipleValuesParams"
    let multipleValuesParams = "";
    Object.keys(queryParams).forEach(param => {
      if (queryParams[param] === "" || queryParams[param].length === 0) {
        // Removing empty params
        delete queryParams[param];
      } else if (param === "status") {
        queryParams[param].forEach(statusObj => {
          multipleValuesParams += `&statuses=${statusObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "roles") {
        queryParams[param].forEach(roleObj => {
          multipleValuesParams += `&roleIds=${roleObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "sort") {
        queryParams[param].forEach(sortStr => {
          multipleValuesParams += `&sort=${sortStr}`;
        });
        delete queryParams[param];
      }
    });

    return `${new URLSearchParams(queryParams).toString()}${multipleValuesParams}`;
  };

  const {
    data: usersStatuses
  } = useGetUsersStatuses({
    userType: USER_TYPES.SUPPLIER
  }, {
    onError: err => handleErrors(err)
  });

  const {
    data: usersRoles
  } = useGetUsersRoles({
    userType: USER_TYPES.SUPPLIER
  }, {
    onError: err => handleErrors(err)
  });

  const {
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers
  } = useGetSupplierUsers(constructQueryParamsString(),
    {
      onSuccess: (data) => {
        const { page } = data;
        const pageContent = page?.content ?? [];
        const suppliers = pageContent.map((supplier) => {
          return {
            id: supplier?.id,
            supplier: supplier?.supplierName,
            supplierId: supplier?.supplierId,
            username: supplier?.username,
            firstName: supplier?.firstName,
            lastName: supplier?.lastName,
            status: supplier?.status,
            lastLogin: formatDate(supplier?.lastLogin ?? null),
            roles: supplier?.roles
          };
        });
        setSuppliersList(suppliers);
        if (suppliers.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && dataTableState.queryPageIndex > 0) {
          // Case when page size is changed and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => handleErrors(err),
      cacheTime: 0,
      enabled: usersStatuses !== null && typeof usersStatuses !== "undefined"
    }
  );

  const handleErrors = (error) => {
    let errorMessage = error.response?.status === 409 &&
    error.response?.data?.errorCode === "object-conflict" ?
      t("messages.duplicatedUsername") : error.response?.data?.parameters?.message ??
      error.message;

    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const handleAddNewSupplier = () => {
    onSupplierModalOpen();
  };

  const closeSupplierModal = () => {
    setSupplierModalData(undefined);
    onSupplierModalClose();
  };

  const handleSupplierModalSubmit = (id, data) => {
    dispatch(showAppSpinner());

    if (id !== undefined) {
      updateSupplierUser({ id, data });
    } else {
      createSupplierUser(data);
    }
  };

  const getRequestConfig = (successMessage) => {
    return {
      onSuccess: () => {
        if (isSupplierModalOpen) {
          closeSupplierModal();
        }
        showSuccessToast(toast, successMessage);
        queryClient.invalidateQueries(supplierAdministrationKeys.all());
      },
      onError: err => handleErrors(err),
      onSettled: () => {
        dispatch(hideAppSpinner());
      }
    };
  };

  const { mutate: createSupplierUser } = useCreateSupplierUser(getRequestConfig(t("messages.createdUser")));
  const { mutate: updateSupplierUser } = useUpdateSupplierUser(getRequestConfig(t("messages.updatedUser")));
  const { mutate: resendSupplierInvitation } = useResendSupplierInvitation(getRequestConfig(t("messages.invitationSent")));
  const { mutate: activateUser } = useActivateUser(getRequestConfig(t("messages.activatedUser")));
  const { mutate: deactivateUser } = useDeactivateUser(getRequestConfig(t("messages.deactivatedUser")));

  const columns = useMemo(() => {
      function handleResendInvitation(data) {
        dispatch(showAppSpinner());
        resendSupplierInvitation(data?.row?.original?.id);
      }

      function handleManageSupplierUser(data) {
        const {
          id, supplierId, firstName, lastName, roles
        } = data.row.original;
        setSupplierModalData({
          id, supplierId, firstName, lastName,
          roles: roles.map(roleObj => {
            return {
              label: t(`roles.${roleObj?.role}`, { defaultValue: roleObj?.role }),
              value: roleObj?.id
            };
          })
        });
        onSupplierModalOpen();
      }

      function handleActivateUser(data) {
        dispatch(showAppSpinner());
        activateUser(data?.row?.original?.id);
      }

      function handleDeactivateUser(data) {
        dispatch(showAppSpinner());
        deactivateUser(data?.row?.original?.id);
      }

      return [
        {
          header: t("gridHeaders.supplier"),
          accessorKey: "supplier",
          size: 165,
          sortDirection: getSortDirection(queryPageSortBy, "supplier")
        },
        {
          header: t("gridHeaders.username"),
          accessorKey: "username",
          size: 165,
          sortDirection: getSortDirection(queryPageSortBy, "username")
        },
        {
          header: t("gridHeaders.firstName"),
          accessorKey: "firstName",
          size: 130,
          sortDirection: getSortDirection(queryPageSortBy, "firstName")
        },
        {
          header: t("gridHeaders.lastName"),
          accessorKey: "lastName",
          size: 130,
          sortDirection: getSortDirection(queryPageSortBy, "lastName")
        },
        {
          header: t("gridHeaders.userRole"),
          accessorKey: "roles",
          size: 180,
          isSortable: false,
          enableColumnFilter: false,
          cell: cell => (
            <Box>
              {cell.row.original?.roles?.map(roleObj => t(`roles.${roleObj.role}`)).join(", ") ?? ""}
            </Box>
          )
        },
        {
          header: t("gridHeaders.userStatus"),
          accessorKey: "status",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "activeStatus"),
          filter: {
            params: {
              options: usersStatuses && usersStatuses.statuses?.length > 0 ?
                usersStatuses.statuses.map(el => {
                  return {
                    label: t(`statuses.${el}`, { defaultValue: el }),
                    value: el
                  };
                }) : [],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          },
          cell: cell => (
            <Badge
              backgroundColor={cell.row.original.status === USER_STATUSES.ACTIVE ? "green.100" : "red.100"}
              color={cell.row.original.status === USER_STATUSES.ACTIVE ? "green.800" : "red.800"}
              height="max-content"
            >
              {cell.row.original.status}
            </Badge>
          )
        },
        {
          header: t("gridHeaders.lastLogin"),
          accessorKey: "lastLogin",
          size: 120,
          enableColumnFilter: false,
          isSortable: false
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 80,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  isDisabled={row.row.original.status === USER_STATUSES.DEACTIVATED}
                  icon={<FaEnvelope color="#6EA161" />}
                  onClick={() => handleResendInvitation(row)}
                >
                  {t("buttons.resendInvitation")}
                </MenuItem>
                <MenuItem
                  isDisabled={row.row.original.status !== USER_STATUSES.ACTIVE}
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleManageSupplierUser(row)}
                >
                  {t("buttons.manageUser")}
                </MenuItem>
                {row.row.original.status === USER_STATUSES.DEACTIVATED && <MenuItem
                  icon={<FaPlay color="#6EA161" />}
                  onClick={() => handleActivateUser(row)}
                >
                  {t("buttons.activateUser")}
                </MenuItem>}
                {row.row.original.status === USER_STATUSES.ACTIVE &&
                  <MenuItem
                    icon={<FaTimes color="#6EA161" />}
                    onClick={() => handleDeactivateUser(row)}
                  >
                    {t("buttons.deactivateUser")}
                  </MenuItem>}
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, filterObj, onSupplierModalOpen, usersStatuses, resendSupplierInvitation,
      activateUser, deactivateUser, dispatch]
  );

  return (
    <Box marginBlockStart="5">
      <Center marginBlockEnd="5">
        <Button
          colorScheme="customScheme"
          width="40"
          onClick={handleAddNewSupplier}
        >
          {t("buttons.addNewUser")}
        </Button>
      </Center>

      {isSupplierModalOpen && usersRoles && <SupplierUserModal
        modalData={supplierModalData}
        isOpen={isSupplierModalOpen}
        onClose={closeSupplierModal}
        onSubmit={handleSupplierModalSubmit}
        roles={usersRoles.roles}
      />}

      <DataTable
        data={suppliersList}
        columns={columns}
        isLoading={isLoadingUsers || isFetchingUsers}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => manageDataTableState({
          filterObj: newFilterObj,
          queryPageIndex: 0
        })}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />
    </Box>
  );
}

export default SupplierUsers;